/**
 * Theme colors for JavaScript consumers (Mermaid, Recharts, etc.)
 * Reads from CSS variables at runtime to stay in sync with the theme.
 *
 * All colors are powered by Radix color scales and shadcn/ui semantic tokens.
 * See styles.css for the CSS variable definitions.
 */

import type { ColorIntent, MermaidStyle } from '@deckspace/types';
import type React from 'react';
import { normalizeColor } from './color-normalization';

function getCssVar(name: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return normalizeColor(raw);
}

/**
 * Chart color CSS variables (powered by Radix)
 * These reference CSS vars that automatically adapt to light/dark mode
 */
export const CHART_COLOR_VARS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
] as const;

/**
 * Get chart color CSS variable by index (wraps around)
 */
export function getChartColor(index: number): string {
  return CHART_COLOR_VARS[index % CHART_COLOR_VARS.length] ?? 'var(--chart-1)';
}

/**
 * Get array of chart color CSS variables for N data series
 */
export function getChartColors(count: number): string[] {
  return Array.from({ length: count }, (_, i) => getChartColor(i));
}

/**
 * Get computed chart colors (resolved values, not CSS vars)
 * Use when you need actual color values (e.g., for canvas rendering)
 */
export function getComputedChartColors(count: number): string[] {
  return Array.from({ length: count }, (_, i) => {
    const varName = `--chart-${(i % 8) + 1}`;
    return getCssVar(varName);
  });
}

/**
 * Minimal theme colors for SVG/canvas rendering where inline styles are needed.
 * Uses shadcn/Radix semantic tokens.
 */
export interface ThemeColors {
  border: string;
  /** High-contrast text: legend labels, primary annotations (--foreground) */
  textPrimary: string;
  /** Medium-contrast text: axis ticks, secondary labels (--muted-foreground) */
  textSecondary: string;
  /** Low-contrast text: grid labels, tertiary info (--muted-foreground) */
  textMuted: string;
}

/**
 * Get theme colors as CSS var references for chart rendering.
 * Returns `var(--name)` strings so colors update automatically on theme toggle
 * without needing a React re-render. Works in both HTML style props and SVG
 * presentation attributes (fill, stroke) in modern browsers.
 */
export function getThemeColors(): ThemeColors {
  return {
    border: 'var(--border)',
    textPrimary: 'var(--foreground)',
    textSecondary: 'var(--muted-foreground)',
    textMuted: 'var(--muted-foreground)',
  };
}

export interface MermaidColors {
  nodePrimary: string;
  nodePrimaryText: string;
  nodePrimaryBorder: string;
  nodeSecondary: string;
  nodeSecondaryText: string;
  nodeSecondaryBorder: string;
  nodeTertiary: string;
  nodeTertiaryText: string;
  nodeTertiaryBorder: string;
  line: string;
  labelBg: string;
}

/**
 * Get Mermaid themeVariables config from current theme
 * Uses Radix-powered CSS variables with fallbacks
 */
export function getMermaidThemeVariables() {
  // Helper to get CSS var with fallback chain
  const getVar = (name: string, fallback: string) =>
    getCssVar(name) || getCssVar(fallback) || fallback;

  // Node colors using Radix-powered variables (vibrant step-9 colors)
  const primaryColor = getVar('--mermaid-node-primary', '--cyan-9');
  const primaryTextColor = getVar('--mermaid-node-primary-foreground', 'white');
  const primaryBorderColor = getVar('--mermaid-node-primary-border', '--cyan-10');
  const secondaryColor = getVar('--mermaid-node-secondary', '--violet-9');
  const secondaryTextColor = getVar('--mermaid-node-secondary-foreground', 'white');
  const secondaryBorderColor = getVar('--mermaid-node-secondary-border', '--violet-10');
  const tertiaryColor = getVar('--mermaid-node-tertiary', '--amber-9');
  const tertiaryTextColor = getVar('--mermaid-node-tertiary-foreground', '--amber-12');
  const tertiaryBorderColor = getVar('--mermaid-node-tertiary-border', '--amber-10');

  // Lines and surfaces
  const lineColor = getVar('--mermaid-line', '--slate-9');
  const textColor = getVar('--foreground', '--slate-12');
  const background = getVar('--background', '--slate-1');
  const border = getVar('--border', '--slate-6');
  const muted = getVar('--muted', '--slate-3');
  const labelBg = getVar('--mermaid-label-bg', '--slate-1');

  return {
    // Core theme
    background,
    primaryColor,
    primaryTextColor,
    primaryBorderColor,
    secondaryColor,
    secondaryTextColor,
    secondaryBorderColor,
    tertiaryColor,
    tertiaryTextColor,
    tertiaryBorderColor,

    // Lines and labels
    lineColor,
    textColor,
    // mainBkg controls the default node background in Mermaid's base theme
    // Set to primaryColor so nodes are colorful, not background-colored
    mainBkg: primaryColor,

    // Node defaults
    nodeBkg: primaryColor,
    nodeBorder: primaryBorderColor,
    nodeTextColor: primaryTextColor,

    // Clusters/subgraphs - use muted background so they contrast with colorful nodes
    clusterBkg: muted,
    clusterBorder: border,
    titleColor: textColor,
    edgeLabelBackground: labelBg,

    // Fill types for alternating node colors in certain diagram types
    // Note: WCAG text adjustment happens in MermaidChart.tsx postProcessSvg
    fillType0: primaryColor,
    fillType1: secondaryColor,
    fillType2: tertiaryColor,
    fillType3: primaryColor,
    fillType4: secondaryColor,
    fillType5: tertiaryColor,
    fillType6: primaryColor,
    fillType7: secondaryColor,
  };
}

/**
 * Get Mermaid themeVariables for design preview mode.
 * Uses user-specified colors with neutral gray fallbacks.
 * This bypasses the theme system for iterative UI comparisons.
 */
export function getMermaidDesignPreviewVariables(style: MermaidStyle) {
  // Vibrant colorful defaults that POP (matching our theme)
  const defaults = {
    background: '#ffffff',
    primary: '#0891b2', // cyan-9 equivalent
    primaryText: '#ffffff',
    primaryBorder: '#0e7490', // cyan-10 equivalent
    secondary: '#7c3aed', // violet-9 equivalent
    secondaryText: '#ffffff',
    secondaryBorder: '#6d28d9', // violet-10 equivalent
    tertiary: '#f59e0b', // amber-9 equivalent
    tertiaryText: '#451a03', // amber-12 equivalent (dark text)
    tertiaryBorder: '#d97706', // amber-10 equivalent
    lineColor: '#64748b',
    labelBg: '#ffffff',
    fontFamily: 'ui-sans-serif, system-ui, sans-serif',
  };

  const nodeColors = style.nodeColors ?? {};

  return {
    background: style.background ?? defaults.background,
    primaryColor: nodeColors.primary ?? defaults.primary,
    primaryTextColor: nodeColors.primaryText ?? defaults.primaryText,
    primaryBorderColor: nodeColors.primaryBorder ?? defaults.primaryBorder,
    secondaryColor: nodeColors.secondary ?? defaults.secondary,
    secondaryTextColor: nodeColors.secondaryText ?? defaults.secondaryText,
    secondaryBorderColor: nodeColors.secondaryBorder ?? defaults.secondaryBorder,
    tertiaryColor: nodeColors.tertiary ?? defaults.tertiary,
    tertiaryTextColor: nodeColors.tertiaryText ?? defaults.tertiaryText,
    tertiaryBorderColor: nodeColors.tertiaryBorder ?? defaults.tertiaryBorder,
    lineColor: style.lineColor ?? defaults.lineColor,
    textColor: nodeColors.primaryText ?? defaults.primaryText,
    mainBkg: nodeColors.primary ?? defaults.primary,
    nodeBkg: nodeColors.primary ?? defaults.primary,
    nodeBorder: nodeColors.primaryBorder ?? defaults.primaryBorder,
    nodeTextColor: nodeColors.primaryText ?? defaults.primaryText,
    clusterBkg: style.background ?? defaults.background,
    clusterBorder: nodeColors.primaryBorder ?? defaults.primaryBorder,
    titleColor: nodeColors.primaryText ?? defaults.primaryText,
    edgeLabelBackground: style.labelBackground ?? defaults.labelBg,
    fontFamily: style.fontFamily ?? defaults.fontFamily,
    // Fill types for alternating node colors
    fillType0: nodeColors.primary ?? defaults.primary,
    fillType1: nodeColors.secondary ?? defaults.secondary,
    fillType2: nodeColors.tertiary ?? defaults.tertiary,
    fillType3: nodeColors.primary ?? defaults.primary,
    fillType4: nodeColors.secondary ?? defaults.secondary,
    fillType5: nodeColors.tertiary ?? defaults.tertiary,
    fillType6: nodeColors.primary ?? defaults.primary,
    fillType7: nodeColors.secondary ?? defaults.secondary,
  };
}

/**
 * Get Recharts color palette from current theme
 * Returns computed color values (resolved from CSS variables)
 */
export function getRechartsColors(): string[] {
  return getComputedChartColors(8);
}

/**
 * Get colors based on semantic intent
 * Uses the intent system to apply meaningful colors to visualizations
 */
export function getColorsForIntent(intent?: ColorIntent, count: number = 2): string[] {
  const baseColors = getComputedChartColors(8);

  if (!intent || intent === 'neutral') {
    return baseColors.slice(0, Math.max(count, baseColors.length));
  }

  switch (intent) {
    case 'positive-negative':
      // Green for positive, red for negative (using Radix-powered intent vars)
      return [
        getCssVar('--intent-positive') || getCssVar('--green-9') || '#10b981',
        getCssVar('--intent-negative') || getCssVar('--red-9') || '#ef4444',
        ...baseColors.slice(2),
      ].slice(0, count);

    case 'comparison':
      // Blue vs Orange for neutral A/B comparison (Radix blue-9 and orange-9)
      return [
        getCssVar('--blue-9') || baseColors[5] || '#3b82f6',
        getCssVar('--orange-9') || baseColors[6] || '#f97316',
        ...baseColors.slice(2),
      ].slice(0, count);

    case 'diverging':
      // Red -> neutral -> blue gradient for deviation from center
      return generateDivergingPalette(count);

    default:
      return baseColors.slice(0, count);
  }
}

/**
 * Generate a diverging palette (red -> neutral -> blue)
 * Uses Radix scales for consistent appearance in light/dark mode
 */
function generateDivergingPalette(count: number): string[] {
  // Diverging: red-9 -> red-6 -> slate-5 -> blue-6 -> blue-9
  const red9 = getCssVar('--red-9') || '#e11d48';
  const red6 = getCssVar('--red-6') || '#fda4af';
  const slate5 = getCssVar('--slate-5') || '#94a3b8';
  const blue6 = getCssVar('--blue-6') || '#7dd3fc';
  const blue9 = getCssVar('--blue-9') || '#0284c7';

  if (count <= 2) return [red9, blue9];
  if (count === 3) return [red9, slate5, blue9];
  if (count === 5) return [red9, red6, slate5, blue6, blue9];

  // For other counts, interpolate from the 5-color palette
  const fullPalette = [red9, red6, slate5, blue6, blue9];
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    const position = (i / (count - 1)) * (fullPalette.length - 1);
    const index = Math.round(position);
    colors.push(fullPalette[index] ?? slate5);
  }

  return colors;
}

/**
 * Get glow effect style for charts (only when glow-enabled is 1)
 */
export function getGlowStyle(color: string): React.CSSProperties {
  const glowEnabled = getCssVar('--glow-enabled');
  const glowSpread = getCssVar('--glow-spread') || '8px';

  if (glowEnabled !== '1') {
    return {};
  }

  return {
    filter: `drop-shadow(0 0 ${glowSpread} ${color})`,
  };
}

/**
 * Get stroke width from theme
 */
export function getChartStrokeWidth(): number {
  const width = getCssVar('--chart-stroke-width');
  return parseFloat(width) || 2.5;
}
